import type {AnyPageMigrator, SiteMigrator} from '../dataMigrationRunner'
import {multilingualReferenceFixer} from './multilingualReferenceFixer'
import {removeBrokenQueries} from './removeBrokenQueries'
import {removeBrokenMemberPages} from './removeBrokenMemberPages'
import {removeBrokenUnitSizesFromLayout} from './removeBrokenUnitSizesFromLayout'
import {removeBrokenVariantRelations as removeBrokenStyleRelations} from './removeBrokenVariantRelations'
import {removeBrokenTpaSharedState} from './removeBrokenTpaSharedState'
import {fixBrokenConnections} from './fixBrokenConnections'
import {addAppDefinitionIdToAppsData} from './addAppDefinitionIdToAppsData'
import {removeFromConnectionBrokenControllers} from './removeFromConnectionBrokenControllers'
import {removeMetaDataFromMaps} from './removeMetaDataFromMaps'
import {wrapMapsForSignatures} from './wrapMapsForSignatures'
import {removeCorruptedLinksFromStyledText} from './removeCorruptedLinksFromStyledText'
import {customMenusCollectionFixer} from './customMenusCollectionFixer'
import {migrateStylableMenuToContainer} from './migrateStylableMenuToContainer'
import {migrateTPAWidgetToContainer} from './migrateTPAWidgetToContainer'
import {migrateTPASectionToContainer} from './migrateTPASectionToContainer'
import {fixMobileComponentsWithWrongPage as fixMobileComponentsWithWrongParent} from './fixMobileComponentsWithWrongPage'
import {migrateMobileVariant} from './migrateMobileVariant'
import {runComponentSpecificMigrations} from './componentMigrations/runComponentSpecificMigrations'
import {childRefFixer} from './childRefFixer'
import {duplicateAndCrossPageRefFixer} from './duplicateAndCrossPageRefFixer/duplicateAndCrossPageRefFixer'
import {fixBrokenLayout} from './fixBrokenLayout'
import {fixBreakpointsVariantsQuery} from './fixBreakpointsVariantsQuery'
import {fixInvalidBackgroundMediaColor} from './fixInvalidBackgroundMediaColor'
import {fixInvalidMediaContainerWithDividers} from './fixInvalidMediaContainerWithDividers'
import {fixMissingDefaultDesignData} from './fixMissingDefaultDesignData'
import {fixBrokenMenusRefs} from './fixBrokenMenuRefs'
import {fixBrokenStylableMenuSubSubStyles} from './fixBrokenStylableMenuSubSubStyles'
import {colorPaletteMigrator} from './colorPaletteMigrator'
import {fixBrokenScopedLayout} from './fixBrokenScopedLayout'
import {addUsesNewAnimationsFlagToMasterPageData} from './addUsesNewAnimationsFlagToMasterPageData'
import {removeBrokenFixerVersion} from './removeBrokenFixerVersion'
import {removeInvalidStyleRefArr as fixInvalidDefaultStyleInRefArr} from './fixInvalidDefaultStyleInRefArr'
import {addMissingSignatures} from './addMissingSignatures'
import {internalRefGarbageCollector} from './internalRefGarbageCollector'
import {removePermaLinksFixer as removePermaLinks} from './removePermaLinks'
import {migrateRectanglePageToTransparent} from './migrateRectanglePageToTransparent'
import {fixCommonMissingReferences} from './fixCommonMissingReferences'
import {fixInvalidPageUriSEO} from './fixInvalidPageUriSEO'
import {fixCorruptedStyleShadow} from './fixCorruptedStyleShadow'
import {fixPageBackgroundNodeLocation} from './fixPageBackgroundNodeLocation'
import {persistAppsInstallState} from './persistAppsInstallState'
import {migrateAppsStateToFragment} from './migrateAppsStateToFragment'
import {mlTypeMismatchFixer} from './mlTypeMismatchFixer'
import {brokenReferenceFixer} from './brokenReferenceFixer'
import {brokenEffectsReferenceFixer} from './removeTriggersWithBrokenEffectRef'
import {fixDuplicatedChildrenIds} from './fixDuplicatedChildrenIds'
import {fixDifferentIdsInMainMenuWithMultilingual} from './fixDifferentIdsInMainMenuWithMultilingual'
import {alignTranslatedPageDataWithMainLanguage} from './alignTranslatedPageDataWithMainLanguage'
import {fixMissingStyleTypeInSystemStyle} from './fixMissingStyleTypeInSystemStyle'
import {fixSystemStyleRefArray} from './fixSystemStyleRefArray'
import {fixTpas0SystemStyleMissingSkin} from './fixTpas0SystemStyleMissingSkin'
import {removeRefComponentChildrenWithoutCorrespondingSlots} from './removeRefComponentChildrenWithoutCorrespondingSlots'
import {deleteMegaMenusInMobile} from './deleteMegaMenusInMobile'
import {brokenRefArrayFixer} from './brokenRefArrayFixer'
import {variantNoRefOwnerFixer} from './variantNoRefOwnerFixer'
import {fixSingleLayoutData} from './fixSingleLayoutData'
import {fixContextMethodName} from './fixContextMethodName'
import {removePageDataWithNoStructure} from './removePageDataWithNoStructure'
import {cleanupAtomicScopesGarbage} from './cleanupAtomicScopesGarbage'
import {cleanManifestHiddenProperties} from './cleanManifestHiddenProperties'
import {migrateStaticSlotsToDynamic} from './migrateStaticSlotsToDynamic'
import {fixComponentStyleProperties} from './fixComponentStyleProperties'

const defaultPageMigrations: AnyPageMigrator[] = [
    removePermaLinks,
    removeBrokenQueries,
    removeBrokenMemberPages,
    removePageDataWithNoStructure,
    removeBrokenUnitSizesFromLayout,
    removeBrokenStyleRelations,
    removeFromConnectionBrokenControllers,
    removeCorruptedLinksFromStyledText,
    fixInvalidPageUriSEO,
    fixPageBackgroundNodeLocation,
    runComponentSpecificMigrations,
    migrateStylableMenuToContainer,
    migrateTPAWidgetToContainer,
    migrateTPASectionToContainer,
    fixMobileComponentsWithWrongParent,
    fixBrokenLayout,
    migrateMobileVariant,
    fixBrokenStylableMenuSubSubStyles,
    duplicateAndCrossPageRefFixer,
    customMenusCollectionFixer,
    fixBrokenScopedLayout,
    fixInvalidMediaContainerWithDividers,
    fixMissingDefaultDesignData,
    fixMissingStyleTypeInSystemStyle,
    fixSystemStyleRefArray,
    fixCommonMissingReferences,
    fixBrokenConnections,
    fixBreakpointsVariantsQuery,
    removeBrokenFixerVersion,
    fixInvalidBackgroundMediaColor,
    fixInvalidDefaultStyleInRefArr,
    addMissingSignatures,
    internalRefGarbageCollector,
    migrateRectanglePageToTransparent,
    fixCorruptedStyleShadow,
    alignTranslatedPageDataWithMainLanguage,
    mlTypeMismatchFixer,
    brokenReferenceFixer,
    addAppDefinitionIdToAppsData,
    removeBrokenTpaSharedState,
    fixBrokenMenusRefs,
    removeMetaDataFromMaps,
    wrapMapsForSignatures,
    cleanupAtomicScopesGarbage,
    cleanManifestHiddenProperties,
    childRefFixer,
    addUsesNewAnimationsFlagToMasterPageData,
    colorPaletteMigrator,
    persistAppsInstallState,
    migrateAppsStateToFragment,
    fixDifferentIdsInMainMenuWithMultilingual,
    brokenEffectsReferenceFixer,
    fixDuplicatedChildrenIds,
    fixTpas0SystemStyleMissingSkin,
    removeRefComponentChildrenWithoutCorrespondingSlots,
    deleteMegaMenusInMobile,
    brokenRefArrayFixer,
    variantNoRefOwnerFixer,
    fixSingleLayoutData,
    fixContextMethodName,
    migrateStaticSlotsToDynamic,
    fixComponentStyleProperties
]

const defaultRunOncePerSite: SiteMigrator[] = [multilingualReferenceFixer]
export {defaultPageMigrations, defaultRunOncePerSite}
